import { useState } from 'react'

import { sendTestWebhook } from '../../api/webhooks.ts'
import { WEBHOOK_TYPES } from '../../routes/webhooks/types.ts'
import { CodeBlock } from '../CodeBlock.tsx'

import {
  examplePayloadFor,
  examplePayloadJsonFor,
  momentFor,
  type ExampleStatus,
} from './webhook-example-payloads.ts'
import {
  ExplorerLayout,
  ExplorerOption,
  ExplorerSendSection,
  SectionLabel,
} from './webhook-explorer.tsx'

// The delivery-lifecycle explorer on /docs/webhooks/delivery-statuses, mounted
// from markdown via `::webhook-lifecycle` (src/docs/directives.tsx).
//
// Left: the statuses as a clickable flow. Right: the exact webhook body Curri
// would send at the selected moment — and, signed in, a button that really
// POSTs that body at the caller's configured sandbox endpoint (the service's
// POST /api/webhooks/test) and reports what the endpoint answered. That last
// part is what turns "read about the lifecycle" into "watch my handler take
// every status", which is step 2 of the getting-started flow.
//
// The shell around all of that is shared with the payload events explorer; see
// webhook-explorer.tsx.

const SETUP_PATH = WEBHOOK_TYPES.deliveries.docs.setup
const SANDBOX_VARIANT = WEBHOOK_TYPES.deliveries.variants.sandbox

/** The happy path, in flow order; `canceled` renders apart as the branch. */
const FLOW: readonly ExampleStatus[] = [
  'pending',
  'scheduled',
  'en_route_to_origin',
  'at_origin',
  'en_route_to_destination',
  'at_destination',
  'delivered',
]

const Connector = () => (
  <div aria-hidden="true" style={{ height: 12, width: 1, background: 'var(--curri-border)', marginLeft: 22 }} />
)

const StatusOption = ({
  status,
  active,
  onSelect,
}: {
  status: ExampleStatus
  active: boolean
  onSelect: () => void
}) => (
  <ExplorerOption
    active={active}
    description={momentFor(status).description}
    label={status}
    onSelect={onSelect}
  />
)

export const WebhookLifecycleExplorer = ({
  onNavigate,
}: {
  onNavigate?: (path: string) => void
}) => {
  const [status, setStatus] = useState<ExampleStatus>('pending')

  return (
    <ExplorerLayout
      nav={
        <>
          {FLOW.map((candidate, index) => (
            <div key={candidate}>
              {index > 0 && <Connector />}
              <StatusOption
                active={candidate === status}
                onSelect={() => setStatus(candidate)}
                status={candidate}
              />
            </div>
          ))}
          <SectionLabel topGap={14}>From any status before delivered</SectionLabel>
          <StatusOption
            active={status === 'canceled'}
            onSelect={() => setStatus('canceled')}
            status="canceled"
          />
        </>
      }
      navLabel="Delivery lifecycle"
    >
      <p style={{ margin: 0, fontSize: 14, color: 'var(--curri-text-secondary)' }}>
        {momentFor(status).caption}
      </p>
      <CodeBlock code={examplePayloadJsonFor(status)} language="json" />
      <div style={{ borderTop: '1px solid var(--curri-border)', paddingTop: 12 }}>
        <ExplorerSendSection
          copy={{
            item: 'payload',
            operation: 'test webhook',
            endpoint: 'sandbox endpoint',
          }}
          onNavigate={onNavigate}
          sandboxVariant={SANDBOX_VARIANT}
          selection={status}
          send={apiFetch => sendTestWebhook(apiFetch, examplePayloadFor(status))}
          setupPath={SETUP_PATH}
        />
      </div>
    </ExplorerLayout>
  )
}
